import { collection } from '@/infra/collection'
import DataLoader from 'dataloader'
import { User, type UserProps } from '@/features/iam/users/User'
import { type IamApi } from '@/features/iam/api/iam-api'
import { type UserResource } from '@/features/iam/api/resources/users'
import { type PrimeUserProps } from '@/features/iam'
import { Id } from '@/infra/id'
import { runInAction } from 'mobx'
import { type Observable } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { type WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import type { OnlineStatusForUserFn } from '@/features/messages'
import { toStream } from '@/utils/mobx-util'
import { filterNonNil } from '@/utils/rx-util'

export class UserStore {
  readonly users = collection({
    getModelId: (u) => u.id,
    getDataId: (u) => u.id,
    create: () => {
      // We'll need the ID of the user to check if they're online.
      // This is safe because the observable being passed in isn't subscribed to
      // before the `user` variable is initialized.
      const isOnline$ = toStream(() => user.id, true).pipe(
        filterNonNil(),
        switchMap(this._isUserOnline),
      )

      const user: User = new User(this._isCurrentUser, isOnline$)
      return user
    },
  })

  private readonly _userLoader = new DataLoader<string, User | null>(
    this._fetchUsersCore.bind(this),
  )

  constructor(
    private readonly _api: IamApi,
    private readonly _isCurrentUser: (user: User) => boolean,
    private readonly _isUserOnline: OnlineStatusForUserFn,
    profileUpdated$: Observable<WorkspaceMember>,
  ) {
    // When the profile has been updated, re-fetch the user.
    profileUpdated$.subscribe((member) => {
      void this.fetchUser(member.userId, true)
    })
  }

  /**
   * Overload for fetching a user by specifying the given token.
   * @param id
   * @param token
   * @param signal
   */
  async fetchUserUsingToken(id: string, token: string, signal?: AbortSignal) {
    const resource = await this._api.users.get(id, token, signal)
    const user = this.users.set(mapUserResourceToProps(resource))
    this._userLoader.prime(resource.public_id, user)
    return user
  }

  /**
   * Fetch a user by ID.
   * @param id
   * @param forceFetch
   */
  async fetchUser(id: Id, forceFetch?: boolean) {
    if (!Id.some(id)) {
      return null
    }

    if (forceFetch) {
      this._userLoader.clear(id)
    }
    return this._userLoader.load(id)
  }

  /**
   * Requests a password reset for the given email address.
   * @param email
   * @param workspaceId
   */
  async requestPasswordReset(email: string, workspaceId: Id) {
    return this._api.users.requestPasswordReset(Id.assert(workspaceId), email)
  }

  /**
   * Primes the user with the given props and fetches it in the background
   * if needed.
   * @param props
   */
  prime(props: PrimeUserProps): User {
    const user = this.users.set({
      id: props.id,
    })

    void this.fetchUser(props.id)

    if (user.isFullyLoaded) {
      // The user has already been hydrated from the server, so don't
      // assign any props to it
      return user
    }

    return user.set(props)
  }

  /**
   * Internal method used by the data loader.
   */
  private async _fetchUsersCore(ids: readonly string[]) {
    const resources = await this._api.users.getMany(ids)

    // Mark users that were retrieved as fully loaded.
    return runInAction(() => {
      const users = this.users.set(
        resources.map((r) => (r ? mapUserResourceToProps(r) : null)),
      )
      for (const user of users) {
        if (user) {
          user.markFullyLoaded()
        }
      }
      return users
    })
  }
}

function mapUserResourceToProps(resource: UserResource): UserProps {
  return {
    id: resource.public_id,
    internalId: resource.user_id,
    avatarUrl: resource.picture,
    email: resource.email ?? '',
    authId: resource.auth_id,
    givenName: resource.given_name,
    familyName: resource.family_name,
    phone: resource.phone ?? '',
    platformAdmin: resource.platform_admin ?? false,
  }
}
